import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { addMonths, format } from 'date-fns';
import { FormUtilService, OptionsList, OptionsListMulti } from 'gung-common';
import { SearchRequest } from 'gung-list';
import {
  Assortment,
  AuthService,
  CheckoutObject,
  Customer,
  CustomerService,
  GungModalService,
  OrderRow,
  OrderSubmitService,
  Product,
  ProductService,
  SelectedCustomerService
} from 'gung-standard';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  first,
  forkJoin,
  map,
  Observable,
  of,
  OperatorFunction,
  switchMap
} from 'rxjs';
import {
  OttoOlsenBlockOrder,
  OttoOlsenBlockOrderAgreementsService
} from '../../services/otto-olsen-block-order-agreements.service';
import { GroupedBlockOrderAgreement } from '../../services/otto-olsen-block-order-agreements-list-config.service';

@Component({
  selector: 'otto-olsen-block-order-agreement-create',
  templateUrl: './otto-olsen-block-order-agreement-create.component.html',
  styleUrl: './otto-olsen-block-order-agreement-create.component.scss',
  standalone: false
})
export class OttoOlsenBlockOrderAgreementCreateComponent implements OnInit {
  public form: FormGroup;
  assortment: string;
  customerId: string;
  customers: Customer[];
  public productSelect: Product[] = [];
  saveClick = false;
  rows = [];
  currency: string;

  loading = false;
  public customerOptionsList: OptionsListMulti[] = [];

  dateValues: OptionsList[] = [
    { id: '6', name: this.translateService.instant('6_MONTHS') },
    { id: '12', name: this.translateService.instant('12_MONTHS') },
    { id: '18', name: this.translateService.instant('18_MONTHS') },
    { id: '24', name: this.translateService.instant('24_MONTHS') }
  ];

  search: OperatorFunction<string, readonly { id; name }[]> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter(term => term.length >= 2),
      switchMap(term => {
        const searchRequest: SearchRequest = {
          terms: [...term.split(' ')],
          skip: 0,
          limit: 10,
          customerId: this.customerId,
          assortment: 'sBLANKETORDER'
        };
        return forkJoin([of(term), this.productService.getPagedProducts(searchRequest)]);
      }),
      map(([term, products]) =>
        products.items./* filter(product => new RegExp(term, 'mi').test(product.name)). */ slice(0, 10)
      )
    );

  formatter = (product: Product) => product.id + ' - ' + product.name;

  isEditBlockOrder: boolean = false;

  constructor(
    protected formBuilder: FormBuilder,
    protected formUtilService: FormUtilService,
    protected productService: ProductService,
    protected orderSubmitService: OrderSubmitService,
    protected gungModalService: GungModalService,
    protected router: Router,
    protected authService: AuthService,
    protected translateService: TranslateService,
    protected customerService: CustomerService,
    protected selectedCustomerService: SelectedCustomerService,
    protected route: ActivatedRoute,
    protected ottoOlsenBlockOrderAgreementsService: OttoOlsenBlockOrderAgreementsService
  ) {}

  ngOnInit(): void {
    this.authService
      .getCurrentUser()
      .pipe(first())
      .subscribe(user => {
        this.assortment = user.assortment;
      });
    forkJoin({
      customers: this.customerService.getCustomers().pipe(first()),
      selectedCustomer: this.selectedCustomerService.getSelectedCustomer().pipe(first())
    }).subscribe(({ customers, selectedCustomer }) => {
      this.customerId = selectedCustomer.id;
      this.customers = customers;
      this.customerOptionsList = customers.map(customer => {
        return {
          id: customer.id,
          name: customer.name,
          selected: selectedCustomer.id === customer.id
        };
      });
      const customer = this.customers.find(c => c.id === this.customerId);
      if (customer) {
        this.currency = customer.extra?.kus?.valkod;
      }

      if (!!this.route.snapshot?.params?.id) {
        this.isEditBlockOrder = true;
        this.getBlockOrderData(this.route.snapshot.params.id)
          .pipe(
            first(),
            switchMap(res =>
              forkJoin({
                res: of(res),
                products: this.productService
                  .getFullProductsByIds(res.rows.map(r => r?.extra?.q_gung_ofh?.artnr || ''))
                  .pipe(first())
              })
            )
          )
          .subscribe(({ res, products }) => {
            this.initForm(res);

            for (const row of res.rows) {
              const product = products.find(p => p.id === row?.extra?.q_gung_ofh?.artnr);
              this.addRow(row, product);
            }
          });
      } else {
        this.initForm();
        this.addRow();
      }
    });
  }

  protected initForm(existingBlockOrder?: GroupedBlockOrderAgreement) {
    this.form = this.formBuilder.group({
      OffBeskr: this.formBuilder.control(existingBlockOrder?.offbeskr || null, Validators.required),
      OffertDat: this.formBuilder.control(existingBlockOrder?.agreementDate || null, Validators.required),
      GiltigTomDatum: this.formBuilder.control(existingBlockOrder?.agreementValidToDate || null, Validators.required),
      /* q_jis_reminderdate: this.formBuilder.control(null, Validators.required), */
      dateValues: this.formBuilder.control(null),
      OrdTyp: this.formBuilder.control(existingBlockOrder?.orderType || null, Validators.required),
      /*   offstatus: this.formBuilder.control(null, Validators.required), */
      KundRef2: this.formBuilder.control(existingBlockOrder?.contact || null, Validators.required),
      KundBestNr: this.formBuilder.control(existingBlockOrder?.refNumber || null, Validators.required),
      email1: this.formBuilder.control(existingBlockOrder?.email1 || null, [Validators.required, Validators.email]),
      email2: this.formBuilder.control(existingBlockOrder?.email2 || null, [Validators.required, Validators.email])
    });

    this.form.get('OffertDat')?.valueChanges.subscribe(value => {
      if (this.form.get('OrdTyp').value === '970' && this.form.get('OrdTyp').value === '973') {
        const months = Number(this.form.get('dateValues').value || 0);
        let date = new Date(value || new Date());
        date = addMonths(date, months);
        this.handleDateSelected(date, 'GiltigTomDatum');
      }
    });

    this.form.get('dateValues')?.valueChanges.subscribe(value => {
      const months = Number(value);
      let date = new Date(this.form.get('OffertDat').value || new Date());
      date = addMonths(date, months);
      this.handleDateSelected(date, 'GiltigTomDatum');
    });
  }

  public setSelectedCustomerId(value: string[]): void {
    if (value?.length > 0 && value[0]) {
      this.customerId = value[0];
      const customer = this.customers.find(c => c.id === this.customerId);
      if (customer) {
        this.currency = customer.extra?.kus?.valkod;
      }
    } else {
      this.customerId = null;
      this.currency = null;
    }
  }

  handleDateSelected(date: Date, field) {
    if (!date) {
      return;
    }
    const dateString = format(date, 'yyyy-MM-dd');
    this.form.get(field).setValue(dateString);
  }

  selectProduct({ item }, index) {
    this.rows[index].productId = item.id;
    this.rows[index].id = item.id;
  }

  addRow(row?: OttoOlsenBlockOrder, product?: Product) {
    this.productSelect.push(product || null);

    this.rows.push({
      id: row?.id || null,
      extra: {
        procargs: {
          OrdStAntal: row?.extra?.q_gung_ofh?.ordstdantal || null,
          q_oo_ofheoq: row?.extra?.q_gung_ofh?.q_oo_ofheoq || null,
          q_oo_ofhprognose: row?.extra?.q_gung_ofh?.q_oo_ofhprognose || null,
          vb_pris: String(row?.extra?.q_gung_ofh?.vb_pris || '').replace('.', ',') || null,
          offnr: row?.extra?.q_gung_ofh?.offnr || null,
          ordradnr: row?.extra?.q_gung_ofh?.ordradnr || null
        }
      },
      productId: row?.extra?.q_gung_ofh?.artnr || null,
      quantity: row?.extra?.q_gung_ofh?.ordantal || null
    });
  }

  removeRow(index) {
    this.productSelect.splice(index, 1);
    this.rows.splice(index, 1);
  }

  checkRows(): boolean {
    let procargsRowFields = ['q_oo_ofhprognose', 'vb_pris'];
    if (this.form.get('OrdTyp').value === '973') {
      procargsRowFields.push('q_oo_ofheoq');
    }
    for (let row of this.rows) {
      if (!row.productId) {
        return false;
      }
      row.extra.procargs.OrdStAntal = row.quantity;
      if (typeof row.extra.procargs.vb_pris === 'string') {
        row.extra.procargs.vb_pris = row.extra.procargs.vb_pris.replace(',', '.');
      }
      for (const field of procargsRowFields) {
        if (!row.extra?.procargs[field]) {
          return false;
        }
      }
    }
    return true;
  }

  cancel() {
    this.router.navigate(['/block-order-agreements']);
  }

  save() {
    this.loading = true;
    this.saveClick = true;
    const checkRows = this.checkRows();
    if (this.form.invalid || !checkRows) {
      this.form.markAllAsTouched();
      this.loading = false;
      return;
    }

    let formData = this.form.getRawValue();

    formData.email = formData.email = [formData.email1 || '', formData.email2 || ''].join(';').replace(/(^;|;$)/g, '');
    delete formData.email1;
    delete formData.email2;
    delete formData.dateValues;

    let payloadBlockOrder: CheckoutObject = {
      rows: this.rows,
      extra: {
        procargs: formData
      },
      targetOrderChannel: 'CREATE_OO_BLOCK_ORDER_AGREEMENT',
      deliveryCustomerId: this.customerId
    };

    if (this.isEditBlockOrder) {
      payloadBlockOrder['orderId'] = this.route?.snapshot?.params?.id;
      payloadBlockOrder.extra.procargs.offnr = this.route?.snapshot?.params?.id;
      payloadBlockOrder.targetOrderChannel = 'UPDATE_OO_BLOCK_ORDER_AGREEMENT';
    }

    const url = 'json/order/create/' + payloadBlockOrder.targetOrderChannel;
    this.orderSubmitService
      .postOrder(url, payloadBlockOrder)
      .pipe(
        first(),
        catchError(error => {
          this.loading = false;

          this.gungModalService.openConfirmYesNoModal(
            'ERROR',
            `${this.translateService.instant('BLOCK_ORDER_CREATION_FAILED')}<br/>${error.error.gungMessage}`,
            { size: 'md' },
            'OK',
            null,
            true
          );
          console.error('Error:', error);
          return EMPTY;
        })
      )
      .subscribe(data => {
        this.loading = false;

        this.gungModalService
          .openConfirmYesNoModal(
            'SUCCESS',
            `${this.translateService.instant('BLOCK_ORDER_CREATED')} ${this.translateService.instant('ORDER')}: ${data.erpOrderIds[0]}`,
            { size: 'sm' },
            'OK',
            null
          )
          .then(() => {
            this.router.navigate(['/block-order-agreements']);
          });
      });
  }

  filterOrderType(data) {
    const allowTypes = ['970', '971', '973'];
    if (allowTypes.includes(data.key)) {
      return true;
    }
    return false;
  }

  allowOnlyOneDecimalSeparator(event: KeyboardEvent, currentValue: string): void {
    const inputChar = event.key;
    const isNumber = /[0-9]/.test(inputChar);
    const isDecimalSeparator = inputChar === '.' || inputChar === ',';

    if (isNumber) {
      return; // Allow digits
    }

    if (isDecimalSeparator) {
      // If a separator already exists (either '.' or ','), prevent another one
      if (currentValue && (currentValue.includes('.') || currentValue.includes(','))) {
        event.preventDefault();
      }
      return; // Allow a single decimal separator
    }

    // Block any other characters (letters, symbols, etc.)
    event.preventDefault();
  }

  getBlockOrderData(orderId: string): Observable<GroupedBlockOrderAgreement> {
    return this.ottoOlsenBlockOrderAgreementsService.getBlockOrders().pipe(
      filter(res => !!res),
      map(rows => {
        const groupedData: Map<string, GroupedBlockOrderAgreement> = new Map<string, GroupedBlockOrderAgreement>();

        for (const row of rows) {
          if (!groupedData.has(row.extra.q_gung_ofh?.offnr)) {
            const group = {
              agreementId: row.extra.q_gung_ofh?.offnr,
              customerId: row.customerId,
              customerName: row.extra.q_gung_ofh?.ftgnamn,
              offbeskr: row.extra.q_gung_ofh?.offbeskr,
              agreementDate: new Date(row.extra.q_gung_ofh?.offertdat),
              agreementValidToDate: new Date(row.extra.q_gung_ofh?.giltigtomdatum),
              status: row.extra.q_gung_ofh?.ofh_offstatus,
              selger: row.extra.q_gung_ofh?.saljare,
              contact: row.extra.q_gung_ofh?.kundref2,
              refNumber: row.extra.q_gung_ofh?.kundbestnr,
              orderType: row.extra.q_gung_ofh?.ofh_ordtyp,
              signingStatus: row.extra.signingStatus,
              email1: row.extra?.q_gung_ofh?.email1,
              email2: row.extra?.q_gung_ofh?.email2,
              rows: []
            };
            groupedData.set(row.extra.q_gung_ofh?.offnr, group);
          }
          groupedData.get(row.extra.q_gung_ofh?.offnr)?.rows.push(row);
        }
        let dataToTable = [...groupedData.values()];
        return dataToTable;
      }),
      switchMap(blockOrders => {
        return of(blockOrders.find(bo => bo.agreementId === orderId));
      })
    );
  }
}
